/**
 * Defined route names for consistency between route definitions and programmatic navigation.
 *
 * NOTE: Some routes may require parameters, and should instead use `routeLink` for programmatic navigation!
 *
 * NOTE: Not all routes must be named safely, but rather only when their name is used programmatically!
 *         Additionally, ensure route names are used with `{name: routeName}` syntax to avoid pathing issues!
 */

// NOTE: ⚠️ Route names **must** be unique for optimal safety/consistency when navigating!
export const routeNames = {
  auth: {
    login: "authLogin",
    logout: "authLogout",
    profile: "authProfile",
    signup: "authSignup",
    verify: "authVerify",
  },
  dashboard: "accountDashboard",
  group: {
    index: "groups",
    create: "groupCreate",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    detail: "groupDetail",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    edit: "groupEdit",
  },
  list: {
    index: "lists",
    create: "listCreate",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    detail: "listDetail",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    edit: "listEdit",
  },
  song: {
    index: "songs",
    create: "songCreate",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    detail: "songDetail",
    /** ⚠️ URL includes `:id` param (use `routeLinks` instead of name!) */
    edit: "songEdit",
  },
} as const;

/**
 * Defined router links for consistency between route definitions and programmatic navigation.
 *
 * Preferred over `routeNames` when parameters are required, as it allows for dynamic URL generation.
 */
export const routeLinks = {
  group: {
    detail: (id: string) => ({ name: routeNames.group.detail, params: { id } }),
    edit: (id: string) => ({ name: routeNames.group.edit, params: { id } }),
  },
  list: {
    add: (query?: { onSave?: "back"; groupId?: string }) => ({
      name: routeNames.list.create,
      query,
    }),
    detail: (id: string) => ({ name: routeNames.list.detail, params: { id } }),
    edit: (id: string) => ({ name: routeNames.list.edit, params: { id } }),
  },
  song: {
    add: (query?: { onSave?: "back"; listIds?: string[] }) => ({
      name: routeNames.song.create,
      query,
    }),
    detail: (id: string) => ({ name: routeNames.song.detail, params: { id } }),
    edit: (id: string) => ({ name: routeNames.song.edit, params: { id } }),
  },
} as const;
